import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import {
    fetchGetApplication,
    GetAccountsResponse,
    GetApplicationResponse
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TProductType } from '../../../dashboard/Dashboard.types';

type TAccount = GetAccountsResponse['data']['accounts'][0];
type TApplication = GetApplicationResponse['data'];
type TDashboardStatus =
    | 'overdue'
    | 'pending'
    | 'topup'
    | 'ongoing'
    | 'ended'
    | 'duetoday'
    | 'applyFlexiLoan'
    | 'pendingFlexiLoan'
    | 'deferred'
    | 'declined'
    | 'unknown';

type TDashboardData = {
    status: TDashboardStatus;
    productType: TProductType;
};

export const useGetTabularFlexiDashboardData = (
    accountId: NonNullable<TAccount['account_id']>
): Partial<TDashboardData> => {
    const stateKey = useStateKey();
    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        suspense: true
    });

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        suspense: true
    });

    const account = accountsResponse?.data.accounts.find(
        (account) => account.account_id === accountId
    );

    if (account && applicationResponse) {
        return {
            status: getDashboardStatus({
                account,
                application: applicationResponse.data
            }),
            productType: account.product_type
        };
    } else
        return {
            status: undefined,
            productType: undefined
        };
};

const getDashboardStatus = ({
    account,
    application
}: {
    account: TAccount;
    application: TApplication;
}): TDashboardStatus => {
    const hasOutstandingPrincipal = account.pending_balance!.principal! > 0.1;

    const loanStatus = account.loan_status!;

    const hasFlexiApplication =
        application &&
        application.requested_products!.length > 0 &&
        application.requested_products?.find((requestedProduct) =>
            ['credit_facility', 'flexi24'].includes(
                requestedProduct.product_type!
            )
        );
    const hasOpenApplication = application?.status === 'open';

    if (['overdue', 'pending', 'topup'].includes(loanStatus)) return loanStatus;

    if (loanStatus !== 'ended' && hasOutstandingPrincipal) return 'ongoing';

    if (loanStatus === 'ended') {
        if (hasFlexiApplication) {
            if (hasOpenApplication) return 'pendingFlexiLoan';
            if (['deferred', 'declined'].includes(application.status!))
                return application.status! as TDashboardStatus;
        }
        return 'ended';
    }

    return 'unknown';
};
